import { LinkButton, PageTitle } from '@sleepstation/components'
import { CopyParagraph } from '@sleepstation/components-copy'
import { VLayout } from '@sleepstation/components-layouts'
import { LAYOUT_ALIGN, LAYOUT_SPACING } from '@sleepstation/const'
import { marginBottom, marginTop } from '@sleepstation/style-api-helpers'
import { replaceTokens } from '@sleepstation/utils'
import PropTypes from 'prop-types'
import React from 'react'
import styled, { ThemeProvider } from 'styled-components'
import { includes } from 'ramda'
import { RANGE_EPWORTH } from '../../../../const/epworth'
import infoCardThemeNegative from '../../../../themes/infoCardThemeNegative'
import infoCardThemeNeutral from '../../../../themes/infoCardThemeNeutral'
import infoCardThemePositive from '../../../../themes/infoCardThemePositive'
import { getRangeEpworth } from '../../../../utils/epworth'
import BreakoutModuleNarrower from '../../../shared/breakoutModules/BreakoutModuleNarrower'
import InfoCard from '../../../shared/InfoCards/InfoCard'
import LinkCard from '../../HubModule/HubPage/HubSectionsList/cards/LinkCard'
import ImageCtaCard from './ImageCtaCard'
import ScoreChartWithKey from './ScoreChartWithKey'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const THEMES = {
  [RANGE_EPWORTH.LOW]: infoCardThemePositive,
  [RANGE_EPWORTH.MILD]: infoCardThemeNeutral,
  [RANGE_EPWORTH.MODERATE]: infoCardThemeNeutral,
  [RANGE_EPWORTH.HIGH]: infoCardThemeNegative,
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const CardWrapper = styled.div`
  ${marginTop(0.75)}
  ${marginBottom(0.25)}
`

const Footer = styled.div.attrs({ 'data-cy': `retake-epworth-link` })`
  ${marginTop(0.75)}
  > * {
    width: 100%;
    display: block;
    max-width: 346px;
    margin: 0 auto;
  }
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const HubEpworthQuizResultsPage = ({ content, results }) => {
  const { result } = results

  const title = replaceTokens(content.titleTemplate, results)
  const range = getRangeEpworth(result)
  const explanationAst = content.explanation[range]
  const description = content.description[range]
  const theme = THEMES[range]
  const shouldShowTherapyCard = includes(range, [
    RANGE_EPWORTH.MODERATE,
    RANGE_EPWORTH.HIGH,
  ])
  const shouldShowResourcesCard = includes(range, [RANGE_EPWORTH.LOW])

  const scoreTitle = replaceTokens(content.scoreTemplate, results)

  return (
    <BreakoutModuleNarrower
      hasBottomBorder={false}
      hasGradient={false}
      paddingV={[[0, 0.25]]}
      marginV={[
        [1.5, 2],
        [1.5, 3],
        [2.5, 4],
      ]}
    >
      <VLayout spacing={[LAYOUT_SPACING.LARGE]} align={LAYOUT_ALIGN.STRETCH}>
        <PageTitle align="center">{title}</PageTitle>
        <ThemeProvider theme={theme}>
          <InfoCard title={scoreTitle} bodyAst={explanationAst} />
        </ThemeProvider>
        <ScoreChartWithKey results={results} />
        <CopyParagraph>{description}</CopyParagraph>
        {shouldShowTherapyCard && (
          <CardWrapper>
            <ImageCtaCard {...content.therapyCard} />
          </CardWrapper>
        )}
        {shouldShowResourcesCard && (
          <CardWrapper>
            <LinkCard {...content.resourcesCard} />
          </CardWrapper>
        )}
        <CopyParagraph>{content.buttonText}</CopyParagraph>
        <Footer>
          <LinkButton
            href={content.retakeQuestionnaireButton.url}
            kind="secondary"
          >
            {content.retakeQuestionnaireButton.label}
          </LinkButton>
        </Footer>
      </VLayout>
    </BreakoutModuleNarrower>
  )
}

HubEpworthQuizResultsPage.propTypes = {
  content: PropTypes.object.isRequired,
  results: PropTypes.object.isRequired,
}

export default HubEpworthQuizResultsPage
